import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

/** Sincroniza el DOM contentEditable solo cuando el valor externo cambia y el campo no tiene foco. */
function useSyncContentEditable(ref, value, readOnly) {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || readOnly) return;
    if (document.activeElement === el) return;
    const next = value ?? "";
    if ((el.innerText || "").replace(/\u200b/g, "").replace(/\n+$/, "") !== next.replace(/\n+$/, "")) {
      el.textContent = next;
    }
  }, [value, readOnly, ref]);
}

/**
 * @param {{ tag?: string, className?: string, style?: React.CSSProperties, value: string, onCommit: (t: string) => void, readOnly?: boolean }} props
 */
export function InlineEditableRichText({ tag = "div", className = "", style, value, onCommit, readOnly }) {
  const ref = useRef(null);
  useSyncContentEditable(ref, value, readOnly);

  const commit = useCallback(() => {
    const el = ref.current;
    if (!el || readOnly) return;
    const text = (el.innerText || "").replace(/\u200b/g, "").replace(/\n+$/, "");
    const prev = (value ?? "").replace(/\n+$/, "");
    if (text !== prev) onCommit(text);
  }, [value, onCommit, readOnly]);

  if (readOnly) {
    return React.createElement(tag, { className, style }, value || " ");
  }

  return React.createElement(tag, {
    ref,
    className: cn(className, "outline-none focus:outline-none"),
    style,
    contentEditable: true,
    suppressContentEditableWarning: true,
    "data-inline-edit": "1",
    onBlur: commit,
    onKeyDown: (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        if (ref.current) ref.current.textContent = value ?? "";
        ref.current?.blur();
      }
    },
  });
}

export function InlineEditableBullets({ className, style, value, onCommit, readOnly }) {
  const [local, setLocal] = useState(value ?? "");
  useEffect(() => {
    setLocal(value ?? "");
  }, [value]);

  const commit = useCallback(() => {
    const t = local.replace(/\r\n/g, "\n");
    if (t !== (value ?? "")) onCommit(t);
  }, [local, value, onCommit]);

  if (readOnly) {
    const lines = (value || "").split("\n").filter((line) => line.trim() !== "");
    return (
      <ul className={cn("ob-canvas-body list-disc space-y-0.5 pl-4 [text-align:inherit]", className)} style={style}>
        {lines.length === 0 ? <li className="opacity-50">Elementos de lista</li> : lines.map((line, i) => <li key={i}>{line}</li>)}
      </ul>
    );
  }

  return (
    <textarea
      data-inline-edit="1"
      className={cn(
        "ob-canvas-body w-full min-h-[4.5rem] resize-y bg-transparent outline-none focus:outline-none [text-align:inherit]",
        "border-0 p-0 ring-0 focus:ring-0",
        className
      )}
      style={style}
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={commit}
      placeholder={"Un punto por línea…"}
    />
  );
}
